import React, { useState, useContext, useEffect } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Checkbox, FormControlLabel, Typography, Box, Alert } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useAuth0 } from "@auth0/auth0-react";
import PremPlayersContext from "../../contexts/premplayers-context";
import UserPendingTransactionsContext from "../../contexts/user-pending-transactions-context";
import { LeaderboardContext } from "../../contexts/leaderboard-context";
import { SiteVarsContext } from "../../contexts/sitevars-context";
import { useSiteVarsFunctions } from "../../hooks/use-site-vars-functions";
import { confirmTeamResource, getConfirmedTeamResource } from "../../services/transfers.service";
import { getClubOffersResource } from "../../services/offers.service";
import { PlayerImage } from "../player/player-image";

export const ManageTeam = () => {
  const { getPlayerPositionById, getTeamById, getCurrentWindow } = useSiteVarsFunctions();
  const { premPlayersData, isLoadingPremPlayersData } = useContext(PremPlayersContext);
  const { userPendingTransactionsData, isLoadingUserPendingTransactionsData } = useContext(UserPendingTransactionsContext);
  const { leaderboardData } = useContext(LeaderboardContext);
  const { siteVarsData } = useContext(SiteVarsContext);
  const { user } = useAuth0();

  const [userPlayers, setUserPlayers] = useState([]);
  const [wonPlayers, setWonPlayers] = useState([]);
  const [isLoadingTeamData, setIsLoadingTeamData] = useState(true);
  const [selectedPlayers, setSelectedPlayers] = useState({});
  const [isTeamConfirmed, setIsTeamConfirmed] = useState(false);
  const [confirmedPlayerIds, setConfirmedPlayerIds] = useState([]);
  const [acceptedOffers, setAcceptedOffers] = useState([]);
  const [isLoadingAcceptedOffers, setIsLoadingAcceptedOffers] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', text: string }
  const [hasInitializedSelections, setHasInitializedSelections] = useState(false);

  const isPastDeadline = Boolean(siteVarsData?.game_vars?.windowFinalized);

  useEffect(() => {
    if (user && leaderboardData.length > 0 && premPlayersData.length > 0) {
      const clubId = user['https://tlog-online.co.uk/club_id'];
      let userTeam = leaderboardData.find(team => team.clubId == clubId);
      if (userTeam && userTeam.players) {
        const filtered = userTeam.players.filter(player => player.transfer != "in");
        const players = filtered.map(player => {
          const fullPlayerData = premPlayersData.find(p => p.playerId === player.playerId);
          return { ...player, ...fullPlayerData };
        });
        setUserPlayers(players);
      }
      setIsLoadingTeamData(false);
    }
  }, [user, leaderboardData, premPlayersData]);

  useEffect(() => {
    const fetchConfirmedTeam = async () => {
      if (user && siteVarsData && siteVarsData.game_vars) {
        const clubId = user['https://tlog-online.co.uk/club_id'];
        const windowId = siteVarsData.game_vars.currentWindow;
        const { data, error } = await getConfirmedTeamResource(clubId, windowId);

        if (data && data.team && data.team.length > 0) {
          setIsTeamConfirmed(true);
          setConfirmedPlayerIds(data.team);
        } else if (error) {
          console.error("Error fetching confirmed team:", error);
        }
      }
    };

    fetchConfirmedTeam();
  }, [user, siteVarsData]);

  useEffect(() => {
    const fetchAcceptedOffers = async () => {
      if (user && siteVarsData && siteVarsData.game_vars && premPlayersData.length > 0) {
        const clubId = user['https://tlog-online.co.uk/club_id'];
        const windowId = siteVarsData.game_vars.currentWindow;
        
        const { data, error } = await getClubOffersResource(clubId, windowId);

        if (data && data.offers) {
          const accepted = data.offers.filter(offer => offer.status == "accepted");
          const offersWithPlayerData = accepted.map(offer => {
            const fullPlayerData = premPlayersData.find(p => p.playerId === offer.playerId);
            return { ...offer, ...fullPlayerData };
          });
          setAcceptedOffers(offersWithPlayerData);
        } else if (error) {
          console.error("Error fetching accepted offers:", error);
        }
        setIsLoadingAcceptedOffers(false);
      }
    };

    fetchAcceptedOffers();
  }, [user, siteVarsData, premPlayersData]);

  useEffect(() => {
    if (userPendingTransactionsData.Items && premPlayersData.length > 0) {
      const wonBids = userPendingTransactionsData.Items.filter(bid => bid.status === "win" && bid.window == getCurrentWindow());
      const players = wonBids.map(bid => {
        const fullPlayerData = premPlayersData.find(p => p.playerId === bid.playerId);
        return { ...bid, ...fullPlayerData };
      });
      setWonPlayers(players);
    }
  }, [userPendingTransactionsData, premPlayersData, getCurrentWindow]);

  const allPlayers = [...userPlayers, ...wonPlayers];
  const clubId = user ? user['https://tlog-online.co.uk/club_id'] : null;

  const playersAfterOffers = allPlayers.filter(player => {
    const transferredFromUserClub = acceptedOffers.some(offer =>
      offer.from == clubId && offer.playerId == player.playerId
    );
    return !transferredFromUserClub;
  });

  const playersAddedByOffers = acceptedOffers.filter(offer =>
    offer.to == clubId && !playersAfterOffers.some(player => player.playerId == offer.playerId)
  ).map(offer => ({
    ...offer,
    isOfferPlayer: true,
  }));

  const finalPlayers = [...playersAfterOffers, ...playersAddedByOffers].sort((a, b) => {
    if (a.positionId !== b.positionId) {
      return a.positionId - b.positionId;
    }
    return (a.lastName || "").localeCompare(b.lastName || "");
  });

  // Initialize selected players once data is ready
  useEffect(() => {
    if (hasInitializedSelections || finalPlayers.length === 0) return;

    const initial = {};

    if (confirmedPlayerIds.length > 0) {
      // 1. If previously confirmed, restore confirmed players
      confirmedPlayerIds.forEach(id => {
        initial[id] = true;
      });
      // 2. Ensure won players and accepted offers are also selected
      finalPlayers.forEach(p => {
        if (p.bidId || p.isOfferPlayer) {
          initial[p.playerId] = true;
        }
      });
      setSelectedPlayers(initial);
      setHasInitializedSelections(true);
    } else if (!isLoadingTeamData && !isLoadingAcceptedOffers && !isLoadingPremPlayersData) {
      // Auto-select won players, offer players, and current squad up to 11
      let count = 0;
      finalPlayers.forEach(p => {
        if (p.bidId || p.isOfferPlayer) {
          initial[p.playerId] = true;
          count++;
        }
      });
      finalPlayers.forEach(p => {
        if (!initial[p.playerId] && count < 11) {
          initial[p.playerId] = true;
          count++;
        }
      });
      setSelectedPlayers(initial);
      setHasInitializedSelections(true);
    }
  }, [finalPlayers, confirmedPlayerIds, hasInitializedSelections, isLoadingTeamData, isLoadingAcceptedOffers, isLoadingPremPlayersData]);

  const handlePlayerSelect = (playerId, isSelected) => {
    if (isPastDeadline || isSaving) return;
    setSelectedPlayers(prevSelectedPlayers => ({
      ...prevSelectedPlayers,
      [playerId]: isSelected,
    }));
    if (feedback) setFeedback(null);
  };

  const numberOfSelectedPlayers = Object.values(selectedPlayers).filter(Boolean).length;

  const confirmTeam = async () => {
    if (isPastDeadline || isSaving) return;

    const selectedPlayerIds = Object.keys(selectedPlayers)
      .filter((playerId) => selectedPlayers[playerId])
      .map(Number);

    if (selectedPlayerIds.length !== 11) {
      setFeedback({ type: 'error', text: `Please select exactly 11 players (currently ${selectedPlayerIds.length}/11 selected).` });
      return;
    }

    setIsSaving(true);
    setFeedback(null);

    try {
      const clubId = user['https://tlog-online.co.uk/club_id'];
      const windowId = siteVarsData?.game_vars?.currentWindow;
      const { data, error } = await confirmTeamResource(clubId, windowId, selectedPlayerIds);

      if (data && data.success !== false) {
        setIsTeamConfirmed(true);
        setConfirmedPlayerIds(selectedPlayerIds);
        setFeedback({
          type: 'success',
          text: 'Team confirmed successfully! You can make further changes anytime right up until the deadline.'
        });
      } else {
        setFeedback({
          type: 'error',
          text: (data && data.message) || (error && error.message) || 'Error confirming team.'
        });
      }
    } catch (error) {
      console.error("Error confirming team:", error);
      setFeedback({ type: 'error', text: 'An unexpected error occurred while confirming team.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <React.Fragment>
      <Box sx={{ mb: 2, px: 1 }}>
        {feedback && (
          <Alert severity={feedback.type} sx={{ mb: 1.5 }} onClose={() => setFeedback(null)}>
            {feedback.text}
          </Alert>
        )}

        {isPastDeadline ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            The transfer window deadline has passed and squads are locked for Gameweek {siteVarsData?.game_vars?.currentWeek}.
          </Alert>
        ) : isTeamConfirmed ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            ✓ Your 11-player squad is confirmed. You can make changes and re-confirm right up until the deadline.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Please select exactly 11 players and confirm your squad before the deadline.
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Selected Players: {numberOfSelectedPlayers} / 11
            {numberOfSelectedPlayers === 11 ? (
              <span style={{ color: 'green', marginLeft: 8 }}>✓ Ready</span>
            ) : (
              <span style={{ color: '#d32f2f', marginLeft: 8 }}>
                ({numberOfSelectedPlayers < 11 ? `Need ${11 - numberOfSelectedPlayers} more` : `${numberOfSelectedPlayers - 11} too many`})
              </span>
            )}
          </Typography>

          <Button
            variant="contained"
            color={isTeamConfirmed ? "primary" : "success"}
            disabled={numberOfSelectedPlayers !== 11 || isPastDeadline || isSaving}
            onClick={confirmTeam}
          >
            {isSaving ? "Saving..." : isPastDeadline ? "Deadline Passed" : isTeamConfirmed ? "Update Confirmed Team" : "Confirm Team"}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ overflowY: 'auto', marginBottom: 5 }}>
        {(isLoadingPremPlayersData || isLoadingUserPendingTransactionsData || isLoadingTeamData || isLoadingAcceptedOffers) && (
          <Stack spacing={1}>
            <Skeleton variant="rounded" width={"100%"} height={30} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
          </Stack>
        )}
        {(!isLoadingPremPlayersData && !isLoadingUserPendingTransactionsData && !isLoadingTeamData && !isLoadingAcceptedOffers) && (
          <Table sx={{ minWidth: 390 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ paddingLeft: 6, paddingRight: 0, paddingBottom: 0 }}></TableCell>
                <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>Player</TableCell>
                <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>Status</TableCell>
                <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>Keep</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {finalPlayers.map((player) => {
                if (!player) return null;

                const isWonPlayer = Boolean(player.bidId);
                const isOfferPlayer = Boolean(player.isOfferPlayer);
                const isCheckboxDisabled = isWonPlayer || isOfferPlayer || isPastDeadline || isSaving;

                return (
                  <TableRow key={player.playerId}>
                    <TableCell style={{ paddingLeft: 6, paddingRight: 0, paddingBottom: 0 }}>
                      <PlayerImage player={player} />
                    </TableCell>
                    <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>
                      <span>{player.lastName}</span>
                      <br />
                      <span style={{ fontWeight: 'bold' }}>{getPlayerPositionById(player.positionId).name_short}</span> {getTeamById(player.teamId).name_short}
                    </TableCell>
                    <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>
                      {isWonPlayer ? "Won Bid" : isOfferPlayer ? "Accepted Offer" : "Current Team"}
                    </TableCell>
                    <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!selectedPlayers[player.playerId]}
                            onChange={(event) => handlePlayerSelect(player.playerId, event.target.checked)}
                            disabled={isCheckboxDisabled}
                          />
                        }
                        label=""
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </React.Fragment>
  );
};